import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listData, updateData, createData, deleteData, streamAI } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VideoCamera, Images, Megaphone, Image as ImageIcon, Sparkle, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const FMT_ICON = { Reel: VideoCamera, Carousel: Images, Story: Megaphone, Static: ImageIcon };
const FMT_TO_TYPE = { Reel: "tiktok_idea", Carousel: "instagram_post", Story: "instagram_post", Static: "instagram_post" };

export default function CalendarPage() {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const key = ["data", "content_calendar", activeId];
  const { data: items = [] } = useQuery({ queryKey: key, queryFn: () => listData("content_calendar", activeId), enabled: !!activeId });

  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(null);
  const [gen, setGen] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const saveM = useMutation({
    mutationFn: (d) => d.id ? updateData("content_calendar", d.id, d) : createData("content_calendar", { ...d, client_id: activeId }),
    onSuccess: () => { invalidate(); toast.success("Saved"); },
  });
  const delM = useMutation({ mutationFn: (id) => deleteData("content_calendar", id), onSuccess: () => { invalidate(); toast.success("Removed"); } });

  const openEdit = (it) => { setCur({ ...it }); setOpen(true); };
  const openNew = () => { setCur({ day: (items.length + 1), format: "Reel", topic: "", cta: "", caption: "", platform: "Instagram", status: "planned" }); setOpen(true); };

  const generateCaption = async () => {
    setGen(true);
    setCur((c) => ({ ...c, caption: "" }));
    try {
      await streamAI("/ai/generate-stream", {
        client_id: activeId, content_type: FMT_TO_TYPE[cur.format] || "instagram_post",
        brief: `${cur.format} about: ${cur.topic}. Call to action: ${cur.cta}.`,
      }, (chunk) => setCur((c) => ({ ...c, caption: (c.caption || "") + chunk })));
    } catch (e) { toast.error("Generation failed"); }
    setGen(false);
  };

  return (
    <PageContainer>
      <PageHeader eyebrow="Organic Social" title="30-Day Content Calendar"
        subtitle="Plan reels, carousels and stories. Generate on-brand captions with AI for any slot.">
        <Button data-testid="add-content-btn" onClick={openNew} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><Plus size={16} className="mr-1.5" /> Add slot</Button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {items.map((it) => {
          const Icon = FMT_ICON[it.format] || ImageIcon;
          return (
            <div key={it.id} data-testid="calendar-card" onClick={() => openEdit(it)}
              className="card-matte p-4 cursor-pointer group">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold brand-text">DAY {it.day}</span>
                <StatusBadge value={it.status} />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={16} className="text-[#9a9a9a]" />
                <span className="text-xs text-[#9a9a9a]">{it.format}</span>
              </div>
              <div className="text-sm font-medium leading-snug min-h-[40px]">{it.topic}</div>
              <div className="mt-2 text-[11px] text-[#7a7a7a]">CTA: {it.cta}</div>
              {it.caption && <div className="mt-2 text-[11px] text-emerald-400/80 flex items-center gap-1"><Sparkle size={11} weight="fill" /> Caption ready</div>}
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">Day {cur?.day} · {cur?.format}</DialogTitle></DialogHeader>
          {cur && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">Day</Label>
                  <Input type="number" value={cur.day} onChange={(e) => setCur({ ...cur, day: Number(e.target.value) })} className="bg-[#10101A] border-white/10 mt-1" /></div>
                <div><Label className="label-caps">Format</Label>
                  <Select value={cur.format} onValueChange={(v) => setCur({ ...cur, format: v })}>
                    <SelectTrigger className="bg-[#10101A] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#14141F] border-white/10 text-white">
                      {["Reel", "Carousel", "Story", "Static"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
              <div><Label className="label-caps">Topic / Hook</Label>
                <Input value={cur.topic} onChange={(e) => setCur({ ...cur, topic: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" data-testid="content-topic" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">CTA</Label>
                  <Input value={cur.cta} onChange={(e) => setCur({ ...cur, cta: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" /></div>
                <div><Label className="label-caps">Status</Label>
                  <Select value={cur.status} onValueChange={(v) => setCur({ ...cur, status: v })}>
                    <SelectTrigger className="bg-[#10101A] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#14141F] border-white/10 text-white">
                      {["planned", "in_progress", "scheduled", "done"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="label-caps">Caption</Label>
                  <Button data-testid="generate-caption-btn" size="sm" variant="ghost" onClick={generateCaption} disabled={gen}
                    className="h-7 text-[#6B3DFF] hover:text-[#A56CFF] hover:bg-[#6B3DFF]/10">
                    <Sparkle size={14} className={`mr-1 ${gen ? "ai-thinking" : ""}`} weight="fill" /> {gen ? "Generating…" : "AI Generate"}
                  </Button>
                </div>
                <Textarea value={cur.caption || ""} onChange={(e) => setCur({ ...cur, caption: e.target.value })}
                  rows={6} className="bg-[#10101A] border-white/10" data-testid="content-caption" placeholder="Write or generate an on-brand caption…" />
              </div>
            </div>
          )}
          <DialogFooter className="flex sm:justify-between gap-2">
            {cur?.id && <Button variant="ghost" onClick={() => { delM.mutate(cur.id); setOpen(false); }} className="text-red-400 hover:text-red-300 hover:bg-red-500/10"><Trash size={16} className="mr-1.5" /> Delete</Button>}
            <Button data-testid="save-content-btn" onClick={() => { saveM.mutate(cur); setOpen(false); }} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Save slot</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
